import '../styles/main.scss';
import * as process from 'process';
import * as Sentry from '@sentry/browser';
import { CircularProgress, DatePickers, DriftThemeProvider } from 'drift-ui';
import { I18nextProvider } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { i18n } from 'i18next';
import Config from 'Config';
import FirebaseAnalytics from '@shamrock-core/common/analytics/firebase-analytics';
import React, { ReactElement, useEffect, useState } from 'react';
import Router from './federated-router';
import store, { persistor } from '../app/configureStore';
import { BranchData } from 'app/types';
import { ApplicationStatusDocs } from 'client-experience-toolbox';
import { loadDOLanguageResources } from '../translations/i18n';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';

window['process'] = process;

interface Props {
  i18n: i18n;
  features: string[];
  branchData?: BranchData;
  applicationStatusDocs?: ApplicationStatusDocs;
}

Sentry.init({
  dsn: Config.sentryDSN,
  environment: Config.sentryEnvironment,
  enabled: Config.sentryEnvironment !== 'local',
});

const DigitalOnboardingApp = ({ i18n: i18nFromShell, features, branchData }: Props): ReactElement => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLanguageFilesLoaded, setIsLanguageFilesLoaded] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        FirebaseAnalytics.init(Config.firebaseConfig);
        const user = LocalAuth.getUser();
        if (user) {
          Sentry.setUser({ id: user._id as string, email: user.email as string });
        }
        if (!isLanguageFilesLoaded) {
          loadDOLanguageResources(i18nFromShell);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLanguageFilesLoaded(true);
        setIsLoaded(true);
      }
    };

    init();
  }, [i18nFromShell, isLanguageFilesLoaded]);

  return (
    <ReduxProvider store={store}>
      <PersistGate loading={<CircularProgress />} persistor={persistor}>
        <I18nextProvider i18n={i18nFromShell}>
          <DriftThemeProvider>
            <DatePickers.LocalizationProvider dateAdapter={AdapterDayjs}>
              {isLoaded && <Router features={features} branchData={branchData} />}
            </DatePickers.LocalizationProvider>
          </DriftThemeProvider>
        </I18nextProvider>
      </PersistGate>
    </ReduxProvider>
  );
};

export default DigitalOnboardingApp;
